import React, { useState, useEffect, useRef } from 'react';
import styles from './Count.module.css';

const StatsShowcase = ({
  title = "עסקים שצומחים",
  highlightedWord = "צומחים",
  subtitle = "במהלך השנים האחרונות - אלה הנתונים שעזרתי לבעלי העסקים שעבדתי איתם להשיג",
  stats = [
    {
      value: "30%",
      label: "קיצור בזמני שיחות המכירה",
      description: "כשהלידים מכירים אתכם, השיחה הופכת לקלה וקצרה"
    },
    {
      value: "20+",
      label: "שעות שבועיות",
      description: "שחסכתי לכל בעל עסק שעבד איתי"
    },
    {
      value: "50K+",
      label: "חשיפות חודשיות",
      description: "הנתון הממוצע בין כל הלקוחות שלי לחשיפות בכל חודש"
    },
    {
      value: "300%",
      label: "עלייה בפניות",
      description: "בממוצע תוך 3 חודשים מהרגע שהתחלנו לעבוד"
    }
  ]
}) => {
  const [animatedValues, setAnimatedValues] = useState(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);
  const componentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateNumbers();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, [hasAnimated]);

  const animateNumbers = () => {
    stats.forEach((stat, index) => {
      const numericValue = parseFloat(stat.value.replace(/[^0-9.]/g, ''));
      const prefix = stat.value.match(/^[^0-9]*/)?.[0] || '';
      const suffix = stat.value.match(/[^0-9.]+$/)?.[0] || '';
      
      let start = 0;
      const duration = 2000;
      const increment = numericValue / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= numericValue) {
          start = numericValue;
          clearInterval(timer);
        }
        
        setAnimatedValues((prev) => {
          const newValues = [...prev];
          newValues[index] = prefix + formatNumber(start, numericValue) + suffix;
          return newValues;
        });
      }, 16);
    });
  };

  const formatNumber = (value, target) => {
    if (target < 10) {
      return value.toFixed(1);
    } else if (target >= 1000000) {
      return (value / 1000000).toFixed(1);
    } else if (target >= 1000) {
      return Math.round(value).toLocaleString();
    }
    return Math.round(value).toString();
  };

  const renderTitle = () => {
    const parts = title.split(highlightedWord);
    if (parts.length === 1) {
      return <span className={styles.titleWhite}>{title}</span>;
    }
    
    return (
      <>
        {parts[0] && <span className={styles.titleWhite}>{parts[0]}</span>}
        <span className={styles.titleHighlight}>{highlightedWord}</span>
        {parts[1] && <span className={styles.titleWhite}>{parts[1]}</span>}
      </>
    );
  };

  return (
    <section className={styles.wrapper}>
      <div className={styles.container} ref={componentRef}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {renderTitle()}
          </h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.statsGrid}>
          {stats.map((stat, index) => (
            <div key={index} className={styles.statCard}>
              <div className={styles.statValue}>
                {hasAnimated ? animatedValues[index] : stat.value}
              </div>
              <div className={styles.statLabel}>{stat.label}</div>
              {stat.description && (
                <div className={styles.statDescription}>{stat.description}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsShowcase;